import React, { type ChangeEvent, type FC, useState, useMemo } from 'react';

interface Order {
  id: number;
  orderNumber: number;
  courseName: string;
  term: string;
  email: string;
  mobile: string;
  grade: string;
  status: 'Completed' | 'Pending' | 'Cancelled';
  paymentMethod: string;
  date: string;
  amount: number;
}
//commit

interface FilterData {
  courseName: string;
  email: string;
  mobile: string;
  grade: string;
  term: string;
  status: string;
  date: string;
}

interface SortConfig {
  key: keyof Order;
  direction: 'asc' | 'desc';
}

const Orders: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5); // Changed to 5 to better demonstrate pagination
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  const [filters, setFilters] = useState<FilterData>({
    courseName: '',
    email: '',
    mobile: '',
    grade: '',
    term: '',
    status: '',
    date: '',
  });

  // Add new state for applied filters
  const [appliedFilters, setAppliedFilters] = useState<FilterData>({
    courseName: '',
    email: '',
    mobile: '',
    grade: '',
    term: '',
    status: '',
    date: '',
  });

  // Expanded mock data
  const [orders, setOrders] = useState<Order[]>([
    {
      id: 1,
      orderNumber: 1001,
      courseName: 'Mathematics 101',
      term: 'First',
      email: 'student1@example.com',
      mobile: '+1234567890',
      grade: '9th',
      status: 'Completed',
      paymentMethod: 'Credit Card',
      date: '2024-03-15',
      amount: 99.99
    },
    {
      id: 2,
      orderNumber: 1002,
      courseName: 'Physics 101',
      term: 'Second',
      email: 'student2@example.com',
      mobile: '+0987654321',
      grade: '10th',
      status: 'Pending',
      paymentMethod: 'PayPal',
      date: '2024-03-18',
      amount: 129.99
    },
    {
      id: 3,
      orderNumber: 1003,
      courseName: 'Chemistry 101',
      term: 'First',
      email: 'student3@example.com',
      mobile: '+1122334455',
      grade: '11th',
      status: 'Completed',
      paymentMethod: 'Bank Transfer',
      date: '2024-03-20',
      amount: 89.99
    },
    {
      id: 4,
      orderNumber: 1004,
      courseName: 'Biology 101',
      term: 'Third',
      email: 'student4@example.com',
      mobile: '+5566778899',
      grade: '12th',
      status: 'Cancelled',
      paymentMethod: 'Credit Card',
      date: '2024-03-22',
      amount: 79.99
    },
    {
      id: 5,
      orderNumber: 1005,
      courseName: 'History 101',
      term: 'Second',
      email: 'student5@example.com',
      mobile: '+1231231234',
      grade: '9th',
      status: 'Completed',
      paymentMethod: 'PayPal',
      date: '2024-03-25',
      amount: 69.99
    },
    {
      id: 6,
      orderNumber: 1006,
      courseName: 'Geography 101',
      term: 'First',
      email: 'student6@example.com',
      mobile: '+4564564567',
      grade: '10th',
      status: 'Pending',
      paymentMethod: 'Bank Transfer',
      date: '2024-03-28',
      amount: 89.99
    },
    {
      id: 7,
      orderNumber: 1007,
      courseName: 'Mathematics 201',
      term: 'Third',
      email: 'student7@example.com',
      mobile: '+7897897890',
      grade: '11th',
      status: 'Completed',
      paymentMethod: 'Credit Card',
      date: '2024-04-01',
      amount: 119.99
    },
    {
      id: 8,
      orderNumber: 1008,
      courseName: 'Physics 201',
      term: 'Second',
      email: 'student8@example.com',
      mobile: '+3213213210',
      grade: '12th',
      status: 'Pending',
      paymentMethod: 'PayPal',
      date: '2024-04-03',
      amount: 149.99
    },
    {
      id: 9,
      orderNumber: 1009,
      courseName: 'Chemistry 201',
      term: 'First',
      email: 'student9@example.com',
      mobile: '+6546546543',
      grade: '9th',
      status: 'Cancelled',
      paymentMethod: 'Bank Transfer',
      date: '2024-04-05',
      amount: 99.99
    },
    {
      id: 10,
      orderNumber: 1010,
      courseName: 'Biology 201',
      term: 'Third',
      email: 'student10@example.com',
      mobile: '+9879879876',
      grade: '10th',
      status: 'Completed',
      paymentMethod: 'Credit Card',
      date: '2024-04-08',
      amount: 109.99
    },
    {
      id: 11,
      orderNumber: 1011,
      courseName: 'History 201',
      term: 'Second',
      email: 'student11@example.com',
      mobile: '+1122334455',
      grade: '11th',
      status: 'Pending',
      paymentMethod: 'PayPal',
      date: '2024-04-10',
      amount: 79.99
    },
    {
      id: 12,
      orderNumber: 1012,
      courseName: 'Geography 201',
      term: 'First',
      email: 'student12@example.com',
      mobile: '+5566778899',
      grade: '12th',
      status: 'Completed',
      paymentMethod: 'Bank Transfer',
      date: '2024-04-12',
      amount: 89.99
    }
  ]);

  // Sorting logic
  const handleSort = (key: keyof Order) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Apply sorting
  const sortedOrders = useMemo(() => {
    let sortableOrders = [...orders];
    if (sortConfig !== null) {
      sortableOrders.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableOrders;
  }, [orders, sortConfig]);

  // Apply filters to applied filters state
  const handleSearch = () => {
    setAppliedFilters(filters);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Apply filters based on applied filters state instead of the input state
  const filteredOrders = useMemo(() => {
    return sortedOrders.filter((order: Order) => {
      return (
        (appliedFilters.courseName === '' || order.courseName.toLowerCase().includes(appliedFilters.courseName.toLowerCase())) &&
        (appliedFilters.email === '' || order.email.toLowerCase().includes(appliedFilters.email.toLowerCase())) &&
        (appliedFilters.mobile === '' || order.mobile.includes(appliedFilters.mobile)) &&
        (appliedFilters.grade === '' || order.grade === appliedFilters.grade) &&
        (appliedFilters.term === '' || order.term === appliedFilters.term) &&
        (appliedFilters.status === '' || order.status === appliedFilters.status) &&
        (appliedFilters.date === '' || order.date.includes(appliedFilters.date))
      );
    });
  }, [sortedOrders, appliedFilters]);

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredOrders.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  return (
    <div className="p-4 md:p-6">
      <h1 className="text-xl md:text-2xl font-bold mb-4 md:mb-6">Orders</h1>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <h2 className="text-lg font-semibold mb-4">Filters</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Course Name</label>
            <input
              type="text"
              value={filters.courseName}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, courseName: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
              placeholder="Filter by course name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="text"
              value={filters.email}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, email: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
              placeholder="Filter by email"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Mobile</label>
            <input
              type="text"
              value={filters.mobile}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, mobile: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
              placeholder="Filter by mobile"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Grade</label>
            <select
              value={filters.grade}
              onChange={(e: ChangeEvent<HTMLSelectElement>) => setFilters({ ...filters, grade: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
            >
              <option value="">All Grades</option>
              <option value="9th">9th Grade</option>
              <option value="10th">10th Grade</option>
              <option value="11th">11th Grade</option>
              <option value="12th">12th Grade</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Term</label>
            <select
              value={filters.term}
              onChange={(e: ChangeEvent<HTMLSelectElement>) => setFilters({ ...filters, term: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
            >
              <option value="">All Terms</option>
              <option value="First">First Term</option>
              <option value="Second">Second Term</option>
              <option value="Third">Third Term</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e: ChangeEvent<HTMLSelectElement>) => setFilters({ ...filters, status: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
            >
              <option value="">All Status</option>
              <option value="Completed">Completed</option>
              <option value="Pending">Pending</option>
              <option value="Cancelled">Cancelled</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
            <input
              type="date"
              value={filters.date}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, date: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <button
            onClick={handleSearch}
            className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors"
          >
            Search
          </button>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-4 border-b">
          <span className="font-medium">Total: {filteredOrders.length}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {[
                  'orderNumber',
                  'courseName',
                  'term',
                  'email',
                  'mobile',
                  'grade',
                  'status',
                  'paymentMethod',
                  'amount',
                  'date',
                ].map((key) => (
                  <th
                    key={key}
                    onClick={() => handleSort(key as keyof Order)}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                  >
                    {key === 'orderNumber' ? 'Order Number' : 
                     key === 'courseName' ? 'Course Name' : 
                     key === 'paymentMethod' ? 'Payment Method' :
                     key.charAt(0).toUpperCase() + key.slice(1)}
                    {sortConfig?.key === key && (
                      <span>{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentItems.map((order: Order) => (
                <tr key={order.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap font-medium">{order.orderNumber}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{order.courseName}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{order.term}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{order.email}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{order.mobile}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{order.grade}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 text-xs rounded-full ${
                        order.status === 'Completed'
                          ? 'bg-green-100 text-green-800'
                          : order.status === 'Pending'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {order.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">{order.paymentMethod}</td>
                  <td className="px-6 py-4 whitespace-nowrap">${order.amount.toFixed(2)}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{order.date}</td>
                </tr>
              ))}
              {currentItems.length === 0 && (
                <tr>
                  <td colSpan={10} className="px-6 py-4 text-center text-gray-500">
                    No orders found matching your criteria
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Enhanced Pagination */}
        {filteredOrders.length > 0 && (
          <div className="px-4 py-3 flex flex-col sm:flex-row items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="mb-4 sm:mb-0">
              <p className="text-sm text-gray-700">
                Showing <span className="font-medium">{indexOfFirstItem + 1}</span> to{' '}
                <span className="font-medium">
                  {Math.min(indexOfLastItem, filteredOrders.length)}
                </span>{' '}
                of <span className="font-medium">{filteredOrders.length}</span> results
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                  className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
                    currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  <span className="sr-only">First</span>
                  <span>&laquo;</span>
                </button>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium ${
                    currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  <span className="sr-only">Previous</span>
                  <span>&lsaquo;</span>
                </button>
                
                {/* Page numbers */}
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(page => 
                    page === 1 || 
                    page === totalPages || 
                    (page >= currentPage - 1 && page <= currentPage + 1)
                  )
                  .map((page, index, array) => {
                    const prevPage = array[index - 1];
                    // Add ellipsis if there's a gap
                    if (index > 0 && prevPage !== page - 1) {
                      return [
                        <span key={`ellipsis-${prevPage}`} className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                          ...
                        </span>,
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium ${
                            currentPage === page ? 'z-10 bg-primary text-white border-primary' : 'bg-white text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      ];
                    }
                    
                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium ${
                          currentPage === page ? 'z-10 bg-primary text-white border-primary' : 'bg-white text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  }).flat()}
                
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium ${
                    currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  <span className="sr-only">Next</span>
                  <span>&rsaquo;</span>
                </button>
                <button
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                  className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
                    currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  <span className="sr-only">Last</span>
                  <span>&raquo;</span>
                </button>
              </nav>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Orders; 